import { Flex, Text, Image, Box } from "@chakra-ui/react";
import BgImage from "@/assets/bg.png";
import TitleHeader from "@/components/TitleHeader";
import { getOrder, OrderDetail, OrderType, Status } from "@/api/order";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { usdcInfo } from "@/constants/tokens";
import {
  accAdd,
  accDiv,
  accMul,
  accSub,
  formatAmountWithFixed,
  toFixed,
} from "@/utils/formatBalance";
import ReceiptTop from "./assets/receipt-top.png";
import ReceiptBottom from "./assets/receipt-bottom.png";
import BaseLogo from "@/assets/base.png";
import { shortenAddress } from "@/utils";
import { avatarImg, consumerEmoji } from "@/utils/avatars";
import dayjs from "dayjs";
import { useUserInfo } from "@/context/UserInfo";
import { useCurrencyExchange } from "@/hooks/useCurrencyExchange";
const Receipt = () => {
  const { orgName, senderAddress } = useUserInfo();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const { usdToCurrency, currencyUnit } = useCurrencyExchange();
  const getOrderDetail = async () => {
    try {
      setLoading(true);
      const order = await getOrder(orderId!);
      setOrderDetail({
        ...order,
        displayAmount: formatAmountWithFixed(
          order.orderAmount,
          usdcInfo.decimals
        ),
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const { isConfirmed, allPayAmount, isNormalPay } = useMemo(() => {
    if (!orderDetail)
      return {
        isConfirmed: false,
        allPayAmount: "0",
        isNormalPay: false,
      };

    const allPayAmount = accAdd(
      accAdd(orderDetail.orderAmount, orderDetail.tipAmount),
      orderDetail.taxAmount
    );

    const isConfirmed = orderDetail.status === Status.Completed;
    const isNormalPay = orderDetail.orderType === OrderType.Normal;

    return {
      isConfirmed,
      allPayAmount,
      isNormalPay,
    };
  }, [orderDetail]);

  useEffect(() => {
    if (!orderId) return;
    getOrderDetail();
    const timer = setTimeout(() => {
      getOrderDetail();
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [orderId]);

  return (
    <Flex
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundImage: `url(${BgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "20px",
      }}
      flexDir={"column"}
      alignItems={"center"}
    >
      <TitleHeader
        onBack={() => navigate("/")}
        title="Receipt"
        color="#D8762B"
        showSwitch={true}
      />
      <Flex
        sx={{
          width: "247px",
          background: `url(${ReceiptTop})`,
          backgroundSize: "100%",
          backgroundPosition: "0 0",
          backgroundRepeat: "no-repeat",
          marginTop: "30px",
          padding: "9px 7px 0px",
        }}
        flexDir={"column"}
      >
        <Flex
          sx={{
            width: "100%",
            backgroundColor: `#FFFFFF`,
            minHeight: "300px",
            color: "#333",
            padding: "30px 20px 40px",
          }}
          flexDir={"column"}
          align={"center"}
        >
          <Text
            sx={{
              fontSize: "40px",
              fontWeight: 900,
              fontFamily: "DIN-Black",
            }}
          >
            {formatAmountWithFixed(
              usdToCurrency(allPayAmount),
              usdcInfo.decimals
            )}
          </Text>
          <Text
            sx={{
              color: "#333",
              fontSize: "24px",
              fontWeight: 500,
            }}
          >
            {currencyUnit}
          </Text>
          {isConfirmed && (
            <Flex
              sx={{
                width: "100%",
                marginTop: "22px",
                borderBottom: "1px dashed #848484",
                paddingBottom: "20px",
              }}
              flexDir={"column"}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                From
              </Text>
              <Flex
                sx={{
                  marginTop: "10px",
                }}
                align={"center"}
              >
                <Text
                  sx={{
                    fontSize: "20px",
                    marginRight: "10px",
                  }}
                >
                  {consumerEmoji}
                </Text>
                <Box>
                  <Text
                    sx={{
                      color: "#848484",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {shortenAddress(orderDetail?.sender?.address || "")}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          )}
          <Flex
            sx={{
              width: "100%",
              marginTop: "22px",
              borderBottom: "1px dashed #848484",
              paddingBottom: "20px",
              position: "relative",
            }}
            flexDir={"column"}
          >
            <Text
              sx={{
                color: "#848484",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              To
            </Text>
            <Flex
              sx={{
                marginTop: "10px",
              }}
              align={"center"}
            >
              <Text
                sx={{
                  fontSize: "20px",
                  marginRight: "10px",
                }}
              >
                {avatarImg(orderDetail?.recipient?.avatar || "")}
              </Text>
              <Box>
                {orgName && (
                  <Text
                    sx={{
                      color: "#333",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {orgName}
                  </Text>
                )}
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {shortenAddress(orderDetail?.recipient?.address || "")}
                </Text>
              </Box>
            </Flex>
          </Flex>
          <Flex
            sx={{
              width: "100%",
              marginTop: "22px",
              borderBottom: "1px dashed #848484",
              paddingBottom: "20px",
              position: "relative",
            }}
            flexDir={"column"}
          >
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#848484",
              }}
            >
              Chain
            </Text>
            <Flex
              sx={{
                marginTop: "10px",
              }}
              align={"center"}
              justify={"space-between"}
            >
              <Image
                src={BaseLogo}
                sx={{
                  width: "20px",
                  height: "20px",
                  marginRight: "4px",
                }}
              ></Image>
              <Text
                sx={{
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                Base
              </Text>
            </Flex>

            <Flex
              justify={"space-between"}
              sx={{
                marginTop: "10px",
              }}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                Subtotal
              </Text>
              <Text
                sx={{
                  color: "#333",
                  fontSize: "14px",
                }}
              >
                {formatAmountWithFixed(
                  usdToCurrency(orderDetail?.orderAmount),
                  usdcInfo.decimals
                )}{" "}
                {currencyUnit}
              </Text>
            </Flex>
            {isNormalPay && (
              <Flex
                justify={"space-between"}
                sx={{
                  marginTop: "10px",
                }}
              >
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  Tax
                </Text>
                <Box
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <Text
                    sx={{
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {formatAmountWithFixed(
                      usdToCurrency(orderDetail?.taxAmount),
                      usdcInfo.decimals
                    )}{" "}
                    {currencyUnit}
                  </Text>
                  <Text
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {toFixed(
                      accMul(
                        accDiv(
                          orderDetail?.taxAmount,
                          orderDetail?.orderAmount
                        ),
                        100
                      ),
                      2
                    )}
                    %
                  </Text>
                </Box>
              </Flex>
            )}
            {isNormalPay && (
              <Flex
                justify={"space-between"}
                sx={{
                  marginTop: "10px",
                }}
              >
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  Tips
                </Text>
                <Box
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <Text
                    sx={{
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {formatAmountWithFixed(
                      usdToCurrency(orderDetail?.tipAmount),
                      usdcInfo.decimals
                    )}{" "}
                    {currencyUnit}
                  </Text>
                  <Text
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {toFixed(
                      accMul(
                        accDiv(
                          orderDetail?.tipAmount,
                          orderDetail?.orderAmount
                        ),
                        100
                      ),
                      2
                    )}
                    %
                  </Text>
                </Box>
              </Flex>
            )}

            <Flex
              justify={"space-between"}
              sx={{
                marginTop: "10px",
              }}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                Fee
              </Text>
              <Box
                sx={{
                  textAlign: "right",
                }}
              >
                <Text
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  -
                  {formatAmountWithFixed(
                    usdToCurrency(orderDetail?.feeAmount),
                    usdcInfo.decimals
                  )}{" "}
                  {currencyUnit}
                </Text>
                <Text
                  sx={{
                    fontSize: "12px",
                  }}
                >
                  1%
                </Text>
              </Box>
            </Flex>
            <Flex
              justify={"space-between"}
              sx={{
                marginTop: "10px",
              }}
            >
              <Text
                sx={{
                  color: "#848484",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                Total
              </Text>
              <Box
                sx={{
                  textAlign: "right",
                }}
              >
                <Text
                  sx={{
                    color: "#333",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  {formatAmountWithFixed(
                    usdToCurrency(
                      accSub(
                        accAdd(
                          orderDetail?.orderAmount,
                          accAdd(orderDetail?.tipAmount, orderDetail?.taxAmount)
                        ),
                        orderDetail?.feeAmount
                      )
                    ),
                    usdcInfo.decimals
                  )}{" "}
                  {currencyUnit}
                </Text>
              </Box>
            </Flex>
          </Flex>
          {isConfirmed && (
            <Flex
              sx={{
                marginTop: "15px",
                width: "100%",
              }}
              justify={"space-between"}
            >
              <Text
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#848484",
                }}
              >
                Time
              </Text>
              <Box
                sx={{
                  textAlign: "right",
                }}
              >
                <Text
                  sx={{
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  {dayjs(orderDetail?.updatedAt).format("YYYY/MM/DD HH:mm")}
                </Text>
                <Text
                  sx={{
                    color: "#848484",
                    fontSize: "12px",
                    fontWeight: 400,
                    fontFamily: "DIN-LightItalic",
                    marginTop: "4px",
                    fontStyle: "italic",
                  }}
                >
                  Received {dayjs(orderDetail?.updatedAt).fromNow()}
                </Text>{" "}
                <Text
                  sx={{
                    color: isConfirmed ? "#00B327" : "#848484",
                    fontSize: "12px",
                    fontWeight: 500,
                    width: "100%",
                    marginTop: "4px",
                  }}
                >
                  {isConfirmed ? "Confirmed" : "Pending"}
                </Text>
              </Box>
            </Flex>
          )}
        </Flex>
        <Image
          src={ReceiptBottom}
          sx={{
            width: "100%",
          }}
        ></Image>
      </Flex>
    </Flex>
  );
};

export default Receipt;
